import { useEffect, useRef } from "react";

import { useGoogleLoginMutation, usePostLoginRedirect } from "@/hooks/use-auth";
import { getApiError } from "@/lib/api/errors";
import { RATE_LIMIT_MESSAGE } from "@/lib/api/error-messages";
import { storage } from "@/lib/storage";
import { loadGoogleIdentityScript } from "@/lib/auth/google-identity";
import { LastUsedTag } from "@/components/auth/last-used-tag";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

// GIS renders at a fixed pixel width within this range; re-render on resize
// so it reads as full width.
const MIN_BUTTON_WIDTH = 200;
const MAX_BUTTON_WIDTH = 400;

interface GoogleSignInButtonProps {
  onError: (message: string) => void;
  lastUsed?: boolean;
}

function messageForGoogleError(error: unknown): string {
  const { status, reason } = getApiError(error);
  if (status === 401 && reason === "email_not_verified") {
    return "Your Google email isn't verified.";
  }
  if (status === 400 || status === 401) {
    return "Couldn't verify your Google sign-in.";
  }
  if (status === 429) return RATE_LIMIT_MESSAGE;
  return "Google sign-in failed. Try again.";
}

/**
 * IMPORTANT: this is the credential (ID-token) flow. One Tap is intentionally
 * never used: we only call renderButton() and NEVER prompt(). The GIS type
 * surface (src/types/google.d.ts) omits prompt() so One Tap can't be invoked.
 */
export function GoogleSignInButton({
  onError,
  lastUsed,
}: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const googleLogin = useGoogleLoginMutation();
  const postLoginRedirect = usePostLoginRedirect();

  // A ref so the GIS init effect runs once (no re-init of the script and
  // ResizeObserver) even if a caller passes a fresh callback each render.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // GIS registers its callback once, so read the latest closure via a ref.
  const handleCredentialRef = useRef<
    (response: GoogleCredentialResponse) => void
  >(() => {});
  handleCredentialRef.current = (response: GoogleCredentialResponse) => {
    googleLogin.mutate(
      { credential: response.credential },
      {
        onSuccess: (data) => {
          // Record before the redirect: a token was issued, so Google was
          // genuinely used even if the redirect fails downstream.
          storage.setLastLoginMethod("google");
          postLoginRedirect(data.token).catch(() =>
            onError("Couldn't finish signing you in.")
          );
        },
        onError: (error) => onError(messageForGoogleError(error)),
      }
    );
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const clientId = GOOGLE_CLIENT_ID;
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    let observer: ResizeObserver | undefined;
    let lastWidth = 0;

    const render = () => {
      if (cancelled || !window.google?.accounts?.id) return;
      const width = Math.round(
        Math.min(
          MAX_BUTTON_WIDTH,
          Math.max(MIN_BUTTON_WIDTH, container.offsetWidth)
        )
      );
      if (width === lastWidth) return;
      lastWidth = width;
      container.innerHTML = "";
      window.google.accounts.id.renderButton(container, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "rectangular",
        logo_alignment: "left",
        width,
      });
    };

    loadGoogleIdentityScript()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          // One Tap is never invoked (we never call prompt()); auto_select off
          // guarantees nothing fires automatically.
          auto_select: false,
          callback: (response) => handleCredentialRef.current(response),
        });
        render();
        observer = new ResizeObserver(() => render());
        observer.observe(container);
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current("Google sign-in is unavailable.");
      });

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, []);

  if (!GOOGLE_CLIENT_ID) {
    console.warn(
      "VITE_GOOGLE_CLIENT_ID is not set, so the Google sign-in button is hidden."
    );
    return null;
  }

  return (
    // The tag is a SIBLING of the GIS container, never a child: GIS wipes the
    // container's innerHTML when it (re)renders, which would delete a nested tag.
    <div className="relative w-full">
      {/* GIS renders real DOM (a div with role="button"), not an iframe, with
          ~4px corners the `shape` option can't change, so override the radius
          to match the shadcn Button. [&>div] covers GIS's outer wrapper in case
          it carries the rounding, and `!` beats GIS's own stylesheet. No
          overflow clip: it would cut the drop shadow into a second outline. */}
      <div
        ref={containerRef}
        className="flex min-h-[44px] w-full items-center [&>div]:rounded-md! [&_[role=button]]:rounded-md!"
      />
      {lastUsed && <LastUsedTag />}
    </div>
  );
}
